import type { BoardData } from '../../services/StorageService'
import {
  buildBestMasonryLayout,
  orderMasonryLayoutItems,
} from '../../utils/masonryLayout'

export type MobileComposePreset = 0 | 1 | 2
export type MobileCornerStyle = 'none' | 'small' | 'rounded'
export type MobileSpacingStyle = 'fitted' | 'light' | 'generous'

export interface MobileBoardImage {
  id: string
  type: 'image'
  src: string
  left: number
  top: number
  width: number
  height: number
  scaleX: number
  scaleY: number
  storagePath?: string
  originalWidth?: number
  originalHeight?: number
  metadata?: {
    source?: 'upload' | 'import'
  }
}

export interface MobileCanvasState {
  version: 'mobile-board-v1'
  template: 'blank'
  orientation: 'landscape'
  width: number
  height: number
  autosnapMode: 'masonry'
  composePreset: MobileComposePreset
  cornerStyle: MobileCornerStyle
  spacingStyle: MobileSpacingStyle
  elements: MobileBoardImage[]
}

type RawCanvasRecord = Record<string, unknown>

export const MOBILE_BOARD_DIMENSIONS = {
  width: 1600,
  height: 1200,
} as const

const OUTER_PADDING = 48
const asRecord = (value: unknown): RawCanvasRecord | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  return value as RawCanvasRecord
}

const parseCanvasState = (value: unknown): RawCanvasRecord | null => {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value))
    } catch {
      return null
    }
  }

  return asRecord(value)
}

const isMobileBoardRecord = (raw: RawCanvasRecord | null): boolean =>
  raw?.version === 'mobile-board-v1'

export const isMobileBoardCanvasState = (value: unknown): boolean =>
  isMobileBoardRecord(parseCanvasState(value))

const createImageId = () => `mobile-image-${crypto.randomUUID()}`

const normalizeImage = (
  rawElement: RawCanvasRecord
): MobileBoardImage | null => {
  const src = typeof rawElement.src === 'string' ? rawElement.src.trim() : ''
  if (!src) return null

  const width =
    typeof rawElement.width === 'number' && Number.isFinite(rawElement.width)
      ? rawElement.width
      : 320
  const height =
    typeof rawElement.height === 'number' && Number.isFinite(rawElement.height)
      ? rawElement.height
      : 420
  const scaleX =
    typeof rawElement.scaleX === 'number' && Number.isFinite(rawElement.scaleX)
      ? rawElement.scaleX
      : 1
  const scaleY =
    typeof rawElement.scaleY === 'number' && Number.isFinite(rawElement.scaleY)
      ? rawElement.scaleY
      : 1

  return {
    id: typeof rawElement.id === 'string' ? rawElement.id : createImageId(),
    type: 'image',
    src,
    left:
      typeof rawElement.left === 'number' && Number.isFinite(rawElement.left)
        ? rawElement.left
        : 0,
    top:
      typeof rawElement.top === 'number' && Number.isFinite(rawElement.top)
        ? rawElement.top
        : 0,
    width,
    height,
    scaleX,
    scaleY,
    storagePath:
      typeof rawElement.storagePath === 'string'
        ? rawElement.storagePath
        : undefined,
    originalWidth:
      typeof rawElement.originalWidth === 'number'
        ? rawElement.originalWidth
        : width,
    originalHeight:
      typeof rawElement.originalHeight === 'number'
        ? rawElement.originalHeight
        : height,
    metadata: {
      source:
        rawElement.metadata &&
        typeof rawElement.metadata === 'object' &&
        !Array.isArray(rawElement.metadata) &&
        'source' in rawElement.metadata &&
        rawElement.metadata.source === 'upload'
          ? 'upload'
          : 'import',
    },
  }
}

const getOrderedImages = (
  images: MobileBoardImage[],
  preset: MobileComposePreset
) => {
  const composeVariantIndex = preset === 1 ? 2 : preset === 2 ? 4 : 0
  return orderMasonryLayoutItems(
    images.map((image) => ({
      id: image.id,
      width: image.originalWidth || image.width || 1,
      height: image.originalHeight || image.height || 1,
    })),
    composeVariantIndex
  )
    .map((item) => images.find((image) => image.id === item.id))
    .filter((image): image is MobileBoardImage => Boolean(image))
}

export const getNextComposePreset = (
  preset: MobileComposePreset
): MobileComposePreset => ((preset + 1) % 3) as MobileComposePreset

export const getNextCornerStyle = (
  style: MobileCornerStyle
): MobileCornerStyle => {
  if (style === 'none') return 'small'
  if (style === 'small') return 'rounded'
  return 'none'
}

export const getNextSpacingStyle = (
  style: MobileSpacingStyle
): MobileSpacingStyle => {
  if (style === 'fitted') return 'light'
  if (style === 'light') return 'generous'
  return 'fitted'
}

export const getCornerRadiusPercent = (style: MobileCornerStyle) => {
  if (style === 'none') return '0%'
  if (style === 'small') return '5%'
  return '12%'
}

export const composeMobileImages = (
  images: MobileBoardImage[],
  options: {
    composePreset: MobileComposePreset
    spacingStyle: MobileSpacingStyle
    width?: number
    height?: number
  }
): MobileBoardImage[] => {
  if (images.length === 0) return []

  const boardWidth = options.width ?? MOBILE_BOARD_DIMENSIONS.width
  const boardHeight = options.height ?? MOBILE_BOARD_DIMENSIONS.height

  if (images.length === 1) {
    const image = images[0]
    const frameWidth = boardWidth - OUTER_PADDING * 2
    const frameHeight = boardHeight - OUTER_PADDING * 2
    return [
      {
        ...image,
        left: OUTER_PADDING,
        top: OUTER_PADDING,
        width: frameWidth,
        height: frameHeight,
        scaleX: 1,
        scaleY: 1,
      },
    ]
  }

  const orderedImages = getOrderedImages(images, options.composePreset)
  const composeVariantIndex =
    options.composePreset === 1 ? 2 : options.composePreset === 2 ? 4 : 0
  const layout = buildBestMasonryLayout(
    orderedImages.map((image) => ({
      id: image.id,
      width: image.originalWidth || image.width || 1,
      height: image.originalHeight || image.height || 1,
    })),
    {
      boardWidth,
      boardHeight,
      optimizationInset: OUTER_PADDING,
      composeVariantIndex,
    }
  )

  if (!layout || layout.length === 0) {
    return images.map((image) => ({ ...image }))
  }

  return layout
    .map((placed) => {
      const image = orderedImages.find(
        (candidate) => candidate.id === placed.id
      )
      if (!image) return null

      return {
        ...image,
        left: Math.round(placed.left),
        top: Math.round(placed.top),
        width: Math.round(placed.width),
        height: Math.round(placed.height),
        scaleX: 1,
        scaleY: 1,
      }
    })
    .filter((image): image is MobileBoardImage => Boolean(image))
}

export const createEmptyMobileCanvasState = (): MobileCanvasState => ({
  version: 'mobile-board-v1',
  template: 'blank',
  orientation: 'landscape',
  width: MOBILE_BOARD_DIMENSIONS.width,
  height: MOBILE_BOARD_DIMENSIONS.height,
  autosnapMode: 'masonry',
  composePreset: 0,
  cornerStyle: 'small',
  spacingStyle: 'light',
  elements: [],
})

export const createEmptyMobileBoardData = (userId: string): BoardData => ({
  title: 'New Board',
  templateType: 'blank',
  canvasState: JSON.stringify(createEmptyMobileCanvasState()),
  userId,
})

export const toMobileCanvasState = (value: unknown): MobileCanvasState => {
  const raw = parseCanvasState(value)
  const base = createEmptyMobileCanvasState()
  if (!raw) return base

  const composePreset =
    typeof raw.composePreset === 'number' &&
    (raw.composePreset === 0 ||
      raw.composePreset === 1 ||
      raw.composePreset === 2)
      ? raw.composePreset
      : 0
  const cornerStyle =
    raw.cornerStyle === 'none' ||
    raw.cornerStyle === 'small' ||
    raw.cornerStyle === 'rounded'
      ? raw.cornerStyle
      : base.cornerStyle
  const spacingStyle =
    raw.spacingStyle === 'fitted' ||
    raw.spacingStyle === 'light' ||
    raw.spacingStyle === 'generous'
      ? raw.spacingStyle
      : base.spacingStyle

  const rawElements = Array.isArray(raw.elements) ? raw.elements : []
  const images = rawElements
    .map((element) => asRecord(element))
    .filter((element): element is RawCanvasRecord => !!element)
    .filter((element) => element.type === 'image')
    .map(normalizeImage)
    .filter((element): element is MobileBoardImage => !!element)

  const width =
    typeof raw.width === 'number' && Number.isFinite(raw.width)
      ? raw.width
      : base.width
  const height =
    typeof raw.height === 'number' && Number.isFinite(raw.height)
      ? raw.height
      : base.height
  const elements = isMobileBoardRecord(raw)
    ? composeMobileImages(images, {
        composePreset,
        spacingStyle,
        width,
        height,
      })
    : images

  return {
    ...base,
    composePreset,
    cornerStyle,
    spacingStyle,
    width,
    height,
    elements,
  }
}

export const serializeMobileCanvasState = (state: MobileCanvasState) =>
  JSON.stringify(state)
